/**
 * Trusted vendors — category chip filter, pinned first, endorsement counts,
 * quick-call. Wired: GET /api/vendors. Port of VendorsScreen in
 * design/source/screens-vendor-issue.jsx.
 */
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Linking, Pressable, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { ArchAvatar, CardSkeletons, ChipsRow, goBack } from '@/features/community/bits';
import { fetchVendors, useFetch, vendorCatLabel, type Vendor } from '@/features/community/lib';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, Chip, EmptyState, Hairline, IconButton, Screen, TopBar } from '@/ui';

const CATEGORIES = ['All', 'Electrician', 'Housekeeping', 'Appliance', 'Gardening', 'Plumbing'];
const CAT_LABEL: Record<string, DictKey> = {
  All: 'filter_all',
  Electrician: 'vendor_cat_electrician',
  Housekeeping: 'vendor_cat_housekeeping',
  Appliance: 'vendor_cat_appliance',
  Gardening: 'vendor_cat_gardening',
  Plumbing: 'vendor_cat_plumbing',
};

function callNumber(phone: string) {
  const clean = phone.replace(/[^+\d]/g, '');
  if (clean) void Linking.openURL(`tel:${clean}`).catch(() => {});
}

export default function VendorsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [cat, setCat] = useState('All');
  const { data, loading, error, refetch } = useFetch(fetchVendors, []);

  const list = (data ?? [])
    .filter((v) => cat === 'All' || v.category === cat)
    .sort((a, b) => Number(b.pinned) - Number(a.pinned));

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('vendor_title')} onBack={goBack} />
      <ChipsRow>
        {CATEGORIES.map((c) => (
          <Chip key={c} label={t(CAT_LABEL[c])} active={cat === c} onPress={() => setCat(c)} />
        ))}
      </ChipsRow>
      <Screen padded>
        {loading ? (
          <CardSkeletons />
        ) : error ? (
          <EmptyState
            icon={<Icon name="phone" size={40} color={colors.textTertiary} />}
            title={t('vendor_err_title')}
            body={t('vendor_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Icon name="phone" size={40} color={colors.textTertiary} />}
            title={t('vendor_empty_title')}
            body={t('vendor_empty_body')}
          />
        ) : (
          <View>
            {list.map((v, i) => (
              <View key={v.id}>
                <VendorRow vendor={v} />
                {i < list.length - 1 ? <Hairline /> : null}
              </View>
            ))}
          </View>
        )}
      </Screen>
    </View>
  );
}

function VendorRow({ vendor }: { vendor: Vendor }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <Pressable onPress={() => router.push(`/vendors/${vendor.id}`)} accessibilityRole="button"
      style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s4, borderRadius: radius.md, backgroundColor: pressed ? colors.canvas1 : 'transparent' })}>
      <ArchAvatar name={vendor.name || '?'} size={44} tone={vendor.pinned ? 'filled' : 'primary'} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <AppText variant="headingSm" weight="500">{vendor.name}</AppText>
          {vendor.pinned ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primary500 }} />
              <AppText variant="label" style={{ color: colors.primary700 }}>{t('vendor_pinned')}</AppText>
            </View>
          ) : null}
        </View>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{vendorCatLabel(vendor.category, t)}</AppText>
        <AppText variant="bodySm" tone="secondary" style={{ marginTop: 4 }}>
          {vendor.endorsements === 1
            ? t('vendor_endorsement', { n: vendor.endorsements })
            : t('vendor_endorsements', { n: vendor.endorsements })}
        </AppText>
      </View>
      {vendor.phone ? (
        <IconButton label={t('vendor_call', { name: vendor.name })} onPress={() => callNumber(vendor.phone)}>
          <Icon name="phone" size={18} color={colors.primary500} />
        </IconButton>
      ) : null}
    </Pressable>
  );
}
